/**
 * Tâches de dossier créées par le manager (table `case_tasks`).
 * Logique pure : visibilité, droits de coche, durées, réducteur realtime, messages d'erreur.
 * Les écritures passent toutes par les RPC SQL (voir CaseTasksContext).
 */

import { isMissingSchemaError, SCHEMA_PENDING_MESSAGE } from "./supabaseErrors";

export const CASE_TASK_TYPES = [
  "qualification_call",
  "phone_followup",
  "send_quote",
  "send_deposit_invoice",
  "send_final_invoice",
] as const;

export type CaseTaskType = (typeof CASE_TASK_TYPES)[number];

export const CASE_TASK_LABELS: Record<CaseTaskType, string> = {
  qualification_call: "Appel de qualification",
  phone_followup: "Relance téléphonique",
  send_quote: "Envoyer le devis",
  send_deposit_invoice: "Envoyer la facture d'acompte",
  send_final_invoice: "Envoyer la facture finale",
};

export const CASE_TASK_NOTE_MAX_LENGTH = 500;

/** Une ligne de `public.case_tasks`. */
export interface CaseTask {
  id: string;
  case_id: string;
  task_type: CaseTaskType;
  note: string | null;
  /** Tâche d'équipe : centre du dossier au moment de la création. */
  assigned_center: string | null;
  /** Tâche « Moi » : le manager qui se l'est assignée. */
  assigned_to: string | null;
  created_by: string;
  created_at: string;
  completed_at: string | null;
  completed_by: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
}

export const CASE_TASK_SELECT =
  "id,case_id,task_type,note,assigned_center,assigned_to,created_by,created_at,completed_at,completed_by,cancelled_at,cancelled_by";

type Centers = ReadonlySet<string> | readonly string[];

function hasCenter(centers: Centers, center: string): boolean {
  return Array.isArray(centers)
    ? (centers as readonly string[]).includes(center)
    : (centers as ReadonlySet<string>).has(center);
}

export function caseTaskLabel(taskType: string): string {
  return CASE_TASK_LABELS[taskType as CaseTaskType] ?? taskType;
}

export function isTaskOpen(task: Pick<CaseTask, "completed_at" | "cancelled_at">): boolean {
  return !task.completed_at && !task.cancelled_at;
}

/**
 * La tâche fait-elle partie du « À faire » de cet utilisateur (indépendamment de son état) ?
 * - tâche d'équipe : l'utilisateur est membre du centre ET n'en est pas l'auteur
 *   (le manager suit ses tâches d'équipe dans l'onglet Manager) ;
 * - tâche « Moi » : assigned_to = l'utilisateur.
 */
export function isTaskForUser(task: CaseTask, userId: string | null | undefined, userCenters: Centers): boolean {
  if (!userId) return false;
  if (task.assigned_to) return task.assigned_to === userId;
  if (!task.assigned_center) return false;
  return task.created_by !== userId && hasCenter(userCenters, task.assigned_center);
}

/** Même règle que la RPC complete_case_task : manager, assigné « Moi » ou membre de l'équipe ; tâche ouverte. */
export function canCompleteTask(
  task: CaseTask,
  userId: string | null | undefined,
  isManager: boolean,
  userCenters: Centers,
): boolean {
  if (!userId || !isTaskOpen(task)) return false;
  if (isManager) return true;
  if (task.assigned_to) return task.assigned_to === userId;
  return !!task.assigned_center && hasCenter(userCenters, task.assigned_center);
}

/** Même règle que la RPC reopen_case_task : l'auteur de la coche ou le manager. */
export function canReopenTask(task: CaseTask, userId: string | null | undefined, isManager: boolean): boolean {
  if (!userId || !task.completed_at) return false;
  return isManager || task.completed_by === userId;
}

/**
 * Durée de traitement en ms : création → coche. Tâche ouverte : création → `now`.
 * Tâche annulée (ou dates invalides) : null.
 */
export function taskDurationMs(task: CaseTask, now: number = Date.now()): number | null {
  const start = Date.parse(task.created_at);
  if (Number.isNaN(start)) return null;
  let end: number;
  if (task.completed_at) end = Date.parse(task.completed_at);
  else if (task.cancelled_at) return null;
  else end = now;
  if (Number.isNaN(end)) return null;
  return Math.max(0, end - start);
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** « 45 min », « 2 h 15 », « 3 j 4 h ». */
export function formatTaskDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < MINUTE) return "< 1 min";
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)} min`;
  if (ms < DAY) {
    const hours = Math.floor(ms / HOUR);
    const minutes = Math.floor((ms % HOUR) / MINUTE);
    return minutes ? `${hours} h ${String(minutes).padStart(2, "0")}` : `${hours} h`;
  }
  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  return hours ? `${days} j ${hours} h` : `${days} j`;
}

export function median(values: readonly number[]): number | null {
  const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

// ─── Réducteur realtime ──────────────────────────────────────────────

/** Plus récentes d'abord, tri stable par id. */
export function sortTasks(tasks: CaseTask[]): CaseTask[] {
  return [...tasks].sort((a, b) =>
    a.created_at === b.created_at ? a.id.localeCompare(b.id) : a.created_at < b.created_at ? 1 : -1,
  );
}

export type TaskChange =
  | { eventType: "INSERT" | "UPDATE"; new: CaseTask }
  | { eventType: "DELETE"; old: Pick<CaseTask, "id"> };

/** Ajoute / remplace / retire une ligne dans une liste (toutes tâches confondues). */
export function applyTaskChange(tasks: CaseTask[], change: TaskChange): CaseTask[] {
  if (change.eventType === "DELETE") {
    return tasks.some((task) => task.id === change.old.id)
      ? tasks.filter((task) => task.id !== change.old.id)
      : tasks;
  }
  const row = change.new;
  const exists = tasks.some((task) => task.id === row.id);
  return sortTasks(exists ? tasks.map((task) => (task.id === row.id ? row : task)) : [...tasks, row]);
}

/** Même chose pour la liste des tâches OUVERTES : une tâche cochée / annulée en sort, une tâche rouverte y revient. */
export function applyOpenTaskChange(openTasks: CaseTask[], change: TaskChange): CaseTask[] {
  if (change.eventType !== "DELETE" && !isTaskOpen(change.new)) {
    return applyTaskChange(openTasks, { eventType: "DELETE", old: { id: change.new.id } });
  }
  return applyTaskChange(openTasks, change);
}

/** Tâche à signaler par le pop-up « nouvelle tâche » : reçue en direct, ouverte, pour moi, pas créée par moi. */
export function isNewTaskForMe(task: CaseTask, userId: string | null | undefined, userCenters: Centers): boolean {
  return !!userId && isTaskOpen(task) && task.created_by !== userId && isTaskForUser(task, userId, userCenters);
}

// ─── Erreurs RPC → français ──────────────────────────────────────────

const ERROR_MESSAGES: Record<string, string> = {
  not_authenticated: "Votre session a expiré. Reconnectez-vous puis réessayez.",
  not_manager: "Action réservée au manager.",
  not_allowed: "Vous n'avez pas le droit de modifier cette tâche.",
  case_not_found: "Dossier introuvable.",
  case_without_center:
    "Ce dossier n'a pas de centre : impossible de l'assigner à une équipe. Renseignez le centre ou choisissez « Moi ».",
  invalid_task_type: "Type de tâche inconnu.",
  note_too_long: `La note ne doit pas dépasser ${CASE_TASK_NOTE_MAX_LENGTH} caractères.`,
  task_not_found: "Tâche introuvable.",
  task_not_open: "Cette tâche est déjà cochée ou annulée.",
  task_not_completed: "Cette tâche n'est pas cochée.",
  invalid_priority: "Valeur de priorité invalide.",
};

export const DEFAULT_TASK_ERROR_MESSAGE = "Une erreur est survenue. Réessayez.";

/** Transforme une erreur Supabase / réseau en message français lisible. */
export function caseTaskErrorMessage(error: unknown): string {
  if (!error) return DEFAULT_TASK_ERROR_MESSAGE;
  const err = error as { message?: unknown; code?: unknown };
  const message = typeof err.message === "string" ? err.message : typeof error === "string" ? error : "";
  const code = typeof err.code === "string" ? err.code : "";
  const key = message.trim();
  if (ERROR_MESSAGES[key]) return ERROR_MESSAGES[key];
  const known = Object.keys(ERROR_MESSAGES).find((name) => new RegExp(`\\b${name}\\b`).test(message));
  if (known) return ERROR_MESSAGES[known];
  if (isMissingSchemaError(error)) return SCHEMA_PENDING_MESSAGE;
  if (code === "23505" || /duplicate key/i.test(message)) return "Cette personne fait déjà partie de l'équipe.";
  if (code === "42501" || /row-level security|permission denied/i.test(message)) return "Action non autorisée.";
  if (/failed to fetch|network|fetch failed|load failed/i.test(message)) {
    return "Connexion impossible. Vérifiez votre réseau et réessayez.";
  }
  return DEFAULT_TASK_ERROR_MESSAGE;
}

/** Erreur levée par les actions du contexte : `message` est déjà en français. */
export class CaseTaskError extends Error {
  readonly original: unknown;
  constructor(original: unknown) {
    super(caseTaskErrorMessage(original));
    this.name = "CaseTaskError";
    this.original = original;
  }
}
